import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Trophy, Target, Award, Timer, Share2, User, Percent, Activity, Sparkles, Star, TrendingUp } from "lucide-react";
import InjuryTimeline from "@/components/app/InjuryTimeline";
import { POSITION_LABEL } from "@/components/familia/PositionPicker";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicPlayerPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/public/players/${token}`);
        setData(data);
      } catch (e) {
        const status = e.response?.status;
        if (status === 404) setError("Este enlace ya no está disponible.");
        else if (status === 403) setError("Este club está temporalmente inactivo.");
        else setError("No se pudo cargar el perfil.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const share = () => {
    const url = window.location.href;
    const text = data ? `Mira las estadísticas de ${data.player.name} en Sudefootball:` : "";
    if (navigator.share) {
      navigator.share({ title: "Sudefootball", text, url }).catch(() => {});
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`;
      window.open(wa, "_blank");
    }
  };

  if (loading) return <PublicShell><div className="text-muted-foreground text-center py-20 text-sm">Cargando…</div></PublicShell>;
  if (error) return <PublicShell><div className="text-center py-20"><div className="overline text-destructive">Aviso</div><div className="font-heading font-black text-3xl uppercase tracking-tighter mt-2">{error}</div></div></PublicShell>;
  if (!data) return null;

  const { player, team, category, club, stats, recent_matches, highlights = [], injuries = [], attendance_monthly = [], seasons = [], momentos = [] } = data;
  const photoSrc = player.photo_url ? `${API_BASE}${player.photo_url}` : null;

  return (
    <PublicShell>
      {/* Hero */}
      <div className="relative border border-border overflow-hidden mb-6">
        <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-primary via-transparent to-transparent" />
        <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6">
          <div className="w-32 h-32 sm:w-36 sm:h-36 border border-border bg-card flex items-center justify-center overflow-hidden shrink-0">
            {photoSrc ? (
              <img src={photoSrc} alt={player.name} className="w-full h-full object-cover" data-testid="public-player-photo" />
            ) : (
              <div className="font-heading font-black text-5xl">{player.jersey_number ?? "—"}</div>
            )}
          </div>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <div className="overline">{club.name} · {category.name}</div>
            <h1 data-testid="public-player-name" className="font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter uppercase mt-1 leading-none break-words">{player.name}</h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap justify-center sm:justify-start text-xs">
              {player.jersey_number != null && <Badge>Dorsal {player.jersey_number}</Badge>}
              {player.position && <Badge>{player.position}</Badge>}
              {player.dominant_foot && <Badge>Pie {player.dominant_foot}</Badge>}
              <Badge>{team.name}</Badge>
            </div>
          </div>
          <div className="flex sm:flex-col gap-2 shrink-0">
            <button
              data-testid="public-share-btn"
              onClick={share}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 border border-primary text-primary hover:bg-primary hover:text-white text-xs uppercase font-heading tracking-wider transition-colors"
            >
              <Share2 size={14} /> Compartir
            </button>
            <a
              data-testid="public-whatsapp-btn"
              href={`https://wa.me/?text=${encodeURIComponent(`Mira las estadísticas de ${player.name} en Sudefootball: ${typeof window !== "undefined" ? window.location.href : ""}`)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white text-xs uppercase font-heading tracking-wider transition-colors"
            >
              <WhatsAppIcon /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Highlights */}
      {stats.racha_semanas >= 2 && (
        <div data-testid="public-racha-badge" className="mb-4 inline-flex items-center gap-2 border border-amber-500/60 bg-amber-500/10 text-amber-500 px-3 py-1.5 text-xs uppercase font-heading font-black tracking-wider">
          🔥 En racha: {stats.racha_semanas} semanas seguidas jugando
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <BigStat icon={Target} label="Goles" value={stats.gols} accent />
        <BigStat icon={Award} label="Asistencias" value={stats.asistencias} />
        <BigStat icon={Trophy} label="Partidos" value={stats.partidos} />
        <BigStat icon={Timer} label="Minutos" value={stats.minutos} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <BigStat icon={Activity} label="Valoración" value={stats.avg_rating} suffix="/5" small />
        <BigStat icon={Percent} label="Asistencia" value={`${stats.pct_asistencia}%`} small />
        <BigStat icon={Target} label="Goles/90'" value={stats.gols_por_90} small />
        <BigStat icon={User} label="Convocatorias" value={stats.convocatorias} small />
      </div>

      {/* Season-over-season comparison */}
      {seasons.length >= 2 && <SeasonComparison seasons={seasons} />}

      {/* Momentos destacados (Plan Familias) */}
      {momentos.length > 0 && (
        <div className="border border-border mb-6" data-testid="public-moments">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Star size={13} className="text-primary" />
            <span className="overline">Momentos destacados</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-border">
            {momentos.map((m, i) => (
              <div key={i} className="bg-card p-3 space-y-1.5" data-testid={`public-moment-${i}`}>
                {m.foto_url && <img src={`${API_BASE}${m.foto_url}`} alt={m.texto} className="w-full h-24 object-cover" loading="lazy" />}
                <div className="text-[10px] font-mono text-muted-foreground">{m.date}{m.minuto !== null && m.minuto !== undefined ? ` · min ${m.minuto}` : ""}</div>
                <div className="text-xs leading-snug">{m.texto}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly attendance — parent panel */}
      {attendance_monthly.length > 0 && (
        <div className="border border-border mb-6" data-testid="public-attendance-monthly">
          <div className="p-4 border-b border-border overline">Asistencia a entrenamientos · por mes</div>
          <div className="divide-y divide-border/40">
            {attendance_monthly.map((m) => (
              <div key={m.month} className="p-3 px-4 flex items-center gap-4" data-testid={`public-att-month-${m.month}`}>
                <div className="w-32 shrink-0 font-heading font-bold text-xs uppercase capitalize">
                  {new Date(m.month + "-01T00:00:00").toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
                </div>
                <div className="flex-1 h-1.5 bg-border">
                  <div className={`h-full ${m.pct >= 80 ? "bg-emerald-500" : m.pct >= 50 ? "bg-amber-400" : "bg-destructive"}`} style={{ width: `${m.pct}%` }} />
                </div>
                <div className="text-right shrink-0">
                  <span className="font-heading font-black text-sm">{m.pct}%</span>
                  <span className="text-[10px] font-mono text-muted-foreground ml-2">{m.present}/{m.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Best matches (auto highlights) */}
      {highlights.length > 0 && (
        <div className="border border-primary/40 bg-primary/5 mb-6" data-testid="public-highlights">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Sparkles size={13} className="text-yellow-400" />
            <span className="overline">Mejores partidos de la temporada</span>
          </div>
          <div className="divide-y divide-border/40">
            {highlights.map((h, i) => (
              <div key={i} className="p-4 flex items-center gap-4" data-testid={`public-highlight-${i}`}>
                <div className={`w-9 h-9 flex items-center justify-center font-heading font-black text-sm ${i === 0 ? "bg-yellow-400 text-black" : i === 1 ? "bg-slate-300 text-black" : "bg-orange-500 text-white"}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-sm uppercase truncate">{h.opponent || "—"}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${h.result === "V" ? "border-emerald-400 text-emerald-400" : h.result === "E" ? "border-yellow-400 text-yellow-400" : "border-red-400 text-red-400"}`}>{h.result} {h.team_score}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{h.date} · {h.minutos}&#39; jugados</div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs font-mono">
                    {h.gols > 0 && <span className="text-primary font-bold">{h.gols} gol{h.gols > 1 ? "es" : ""}</span>}
                    {h.asistencias > 0 && <span className="text-blue-400 font-bold">{h.asistencias} asist.</span>}
                    {h.rating > 0 && <span className="text-yellow-400"><Star size={10} className="inline mb-0.5" /> {h.rating}/5</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Injuries — public-safe (dates + type + severity only, no medical notes) */}
      {injuries.length > 0 && (
        <div className="border border-border mb-8" data-testid="public-injuries">
          <div className="p-4 border-b border-border overline">Historial de lesiones</div>
          <InjuryTimeline injuries={injuries} mode="public" testid="public-injuries-timeline" />
        </div>
      )}

      {/* Recent matches */}
      {recent_matches.length > 0 && (
        <div className="border border-border mb-8" data-testid="public-recent-matches">
          <div className="p-4 border-b border-border overline">Últimos partidos</div>
          <div className="divide-y divide-border/60">
            {recent_matches.map((m, i) => (
              <div key={i} className="flex items-center justify-between p-4 flex-wrap gap-3">
                <div>
                  <div className="font-mono text-xs text-muted-foreground">{m.date}</div>
                  <div className="font-heading font-bold uppercase text-sm mt-1">
                    {m.home_away === "casa" ? "Casa" : m.home_away === "fuera" ? "Fuera" : ""} · {m.opponent || "—"}
                  </div>
                  {m.posicion && <div className="text-[10px] font-mono text-primary mt-0.5">📍 {POSITION_LABEL[m.posicion] || m.posicion}</div>}
                </div>
                <div className="flex items-center gap-4 text-xs font-mono">
                  {m.score && <span className="text-primary font-heading font-black text-2xl">{m.score}</span>}
                  <div className="flex flex-col items-end">
                    <span className="text-[10px] overline">Min</span>
                    <span>{m.minutos}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[10px] overline">G</span>
                    <span>{m.gols}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[10px] overline">A</span>
                    <span>{m.asistencias}</span>
                  </div>
                  {m.rating != null && (
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] overline">Nota</span>
                      <span className="text-primary">{m.rating}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer note */}
      <div className="text-center text-[11px] text-muted-foreground mt-8 pb-8">
        Datos consolidados de la temporada · Perfil generado y gestionado por el entrenador
      </div>
    </PublicShell>
  );
}

function SeasonComparison({ seasons }) {
  const metrics = [
    ["gols", "Goles"],
    ["asistencias", "Asistencias"],
    ["partidos", "Partidos"],
    ["minutos", "Minutos"],
  ];
  const last = seasons[seasons.length - 1];
  const prev = seasons[seasons.length - 2];
  return (
    <div className="border border-border mb-6" data-testid="public-season-comparison">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <TrendingUp size={13} className="text-primary" />
        <span className="overline">Evolución por temporada</span>
      </div>
      <div className="p-4 space-y-5">
        {metrics.map(([key, label]) => {
          const max = Math.max(...seasons.map((s) => s[key] || 0), 1);
          const delta = (last[key] || 0) - (prev[key] || 0);
          return (
            <div key={key} data-testid={`public-season-metric-${key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="overline text-[10px]">{label}</span>
                {delta !== 0 && (
                  <span className={`text-[10px] font-mono font-bold ${delta > 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {delta > 0 ? "▲" : "▼"} {delta > 0 ? "+" : ""}{delta} vs {prev.season}
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {seasons.map((s, i) => {
                  const isLast = i === seasons.length - 1;
                  return (
                    <div key={s.season} className="flex items-center gap-3">
                      <span className={`w-20 shrink-0 font-mono text-[10px] ${isLast ? "text-foreground font-bold" : "text-muted-foreground"}`}>{s.season}</span>
                      <div className="flex-1 h-4 bg-border/40">
                        <div
                          className={`h-full ${isLast ? "bg-primary" : "bg-muted-foreground/40"}`}
                          style={{ width: `${Math.max(4, (100 * (s[key] || 0)) / max)}%` }}
                        />
                      </div>
                      <span className={`w-10 shrink-0 text-right font-heading font-black text-xs leading-none ${isLast ? "text-primary" : "text-muted-foreground"}`}>{s[key] || 0}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function PublicShell({ children }) {
  const SUDEPORTE_LOGO = "/brand/sudefootball-white-40.png";
  return (
    <div className="min-h-screen grain">
      <header className="border-b border-border sticky top-0 z-40 backdrop-blur-md bg-background/80">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
          <div className="flex flex-col items-start leading-none">
            <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-6 w-auto object-contain" />
            <span className="font-heading font-black italic tracking-tight leading-none text-foreground text-[10px] mt-1">sudeporte</span>
          </div>
          </div>
          <div className="overline text-[9px]">Perfil público</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}

function BigStat({ icon: Icon, label, value, suffix = "", accent, small }) {
  return (
    <div className={`border p-4 ${accent ? "border-primary bg-primary/5" : "border-border"}`}>
      <div className="flex items-center gap-1 overline text-[10px]"><Icon size={11} />{label}</div>
      <div className={`font-heading font-black mt-2 leading-none ${small ? "text-2xl" : "text-4xl"} ${accent ? "text-primary" : ""}`}>
        {value ?? 0}{suffix && <span className="text-xs opacity-60 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function Badge({ children }) {
  return <span className="px-2 py-1 border border-border font-mono text-[10px] uppercase">{children}</span>;
}
